import React, { useState } from 'react';
import { Button, Card } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import PageHeader from 'components/common/PageHeader';
import FullPageSpinner from 'components/common/full-page-spinner';
import { usePaginationBe } from 'hooks/useBeHandle';
import { BaseTableRQ } from 'components/common/models/BaseClass';
import { swalConfirm, swalToastSuccess, swalToastError } from 'components/utilities/SwalToastUtil';
import {
  searchOutstandingRequests,
  cancelRequestItems
} from 'services/stock-location/stockRequestService';
import StockRequestFilter from './StockRequestFilter';
import StockRequestTable from './StockRequestTable';
import LanguageSwitcher from './LanguageSwitcher';
import NewStockRequestModal from './NewStockRequest/NewStockRequestModal';

export default function StockRequest() {
  const { t } = useTranslation();

  const defaultRQ = new BaseTableRQ(1, 20);
  const [isLoading, setIsLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [data, setData] = useState([]);
  const [paging, setPaging] = useState({
    ...defaultRQ,
    sortBy: 'dateTimeRequested',
    desc: false
  });
  const [colSort, setColSort] = useState({
    sortBy: 'dateTimeRequested',
    isBeSortedDesc: false,
    isBeSorted: true
  });

  const [filter, setFilter] = useState({
    area: null,
    requestedBy: null,
    emplasCode: '',
    supplierCode: ''
  });

  const [selectedCancelIds, setSelectedCancelIds] = useState([]);
  const [showNewRequest, setShowNewRequest] = useState(false);

  const handleFilterChange = (field, value) => {
    setFilter(prev => ({ ...prev, [field]: value }));
  };

  const buildSearchRQ = pagingRq => {
    const rq = {
      ...pagingRq,
      sortBy:
        pagingRq.sortBy === 'dateTimeRequested' ? undefined : pagingRq.sortBy,
      areaId: filter.area?.value,
      requestedBy: filter.requestedBy?.value,
      emplasCode: filter.emplasCode,
      supplierCode: filter.supplierCode
    };

    return Object.fromEntries(
      Object.entries(rq).filter(
        ([, v]) => v !== null && v !== undefined && v !== ''
      )
    );
  };

  const loadSearch = pagingRq => {
    setIsLoading(true);
    const rq = buildSearchRQ(pagingRq);
    searchOutstandingRequests(rq)
      .then(res => {
        if (!res) return;
        setTotal(res.total);
        setData(res.items);
        const cl = {
          ...pagingRq,
          sortBy: pagingRq.sortBy,
          desc: pagingRq.desc,
          isBeSortedDesc: pagingRq.desc,
          isBeSorted: pagingRq.sortBy !== undefined && pagingRq.sortBy !== ''
        };
        setColSort(cl);
        setPaging(cl);
        setFilter(prev => ({ ...prev, ...cl }));
      })
      .catch(err => {
        console.error('❌ load Stock Request error:', err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleSearch = () => {
    loadSearch({ ...paging, pageNum: 1 });
  };

  const { gotoPage, nextPage, previousPage, handleSort } = usePaginationBe(
    paging,
    total,
    loadSearch,
    filter,
    setFilter
  );

  const handleCancelItems = () => {
    swalConfirm(
      t('cancelItemsConfirmTitle'),
      t('cancelItemsConfirmText'),
      () => {
        setIsLoading(true);
        cancelRequestItems(selectedCancelIds)
          .then(res => {
            if (!res?.success) {
              swalToastError(t('cancelItemsError'));
              return;
            }
            swalToastSuccess(t('cancelItemsSuccess'));
            setSelectedCancelIds([]);
            loadSearch(paging);
          })
          .catch(() => swalToastError(t('cancelItemsError')))
          .finally(() => setIsLoading(false));
      }
    );
  };

  return (
    <div className="stock-request-page">
      <FullPageSpinner show={isLoading} quantity={3} variant="success" />
      <PageHeader
        title={t('outstandingStockRequests')}
        className="mb-3"
        endEl={
          <div className="d-flex gap-2 align-items-center">
            <Button onClick={() => setShowNewRequest(true)}>
              {t('raiseStockRequest')}
            </Button>
            <LanguageSwitcher />
          </div>
        }
      />

      <StockRequestFilter
        filter={filter}
        handleChange={handleFilterChange}
        handleSubmit={handleSearch}
      />

      <Card>
        <Card.Body>
          <StockRequestTable
            data={data}
            paging={{ ...paging, ...filter }}
            total={total}
            nextPage={nextPage}
            previousPage={previousPage}
            gotoPage={gotoPage}
            colSort={colSort}
            handleSort={handleSort}
            selectedCancelIds={selectedCancelIds}
            setSelectedCancelIds={setSelectedCancelIds}
          />

          <div className="pt-3 text-end">
            <Button
              variant="danger"
              disabled={selectedCancelIds.length === 0}
              onClick={handleCancelItems}
            >
              {t('cancelItems')}
            </Button>
          </div>
        </Card.Body>
      </Card>

      <NewStockRequestModal
        isShow={showNewRequest}
        handleClose={() => setShowNewRequest(false)}
        onSubmitted={() => loadSearch(paging)}
      />
    </div>
  );
}
